'use client'

import type { Route } from 'next'
import { useRouter } from 'next/navigation'
import { Clock } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Card, CardContent } from '@/components/ui/card'
import { Progress } from '@/components/ui/progress'
import { routes } from '@/config/routes'
import {
  CASE_STATUS_PIPELINE,
  CaseStatusBadge,
  FLOW_LABEL,
  FlowBadge,
  type CaseRecord,
} from '@/features/cases'

/**
 * Ported from `DashboardHome.tsx`'s `PendingCaseCard` — shown on Home (not the service's own tab,
 * which uses the simpler `PendingRequestCard`) once a case exists for the active flow but isn't
 * published yet, with a progress bar over the pipeline stage. Drops the design's optional
 * `expectedReadyAt` copy variant — that field doesn't exist on this app's `CaseRecord`.
 *
 * Only reads `flow`/`status`, so it's usable from a case-list summary (no `caseStatus`-published
 * detail fetch needed to render this state — see `useMyCaseForFlow`'s doc comment).
 */
export function PendingCaseCard({
  caseRecord,
}: {
  caseRecord: Pick<CaseRecord, 'id' | 'flow' | 'status'>
}) {
  const router = useRouter()
  const stepIndex = CASE_STATUS_PIPELINE.indexOf(caseRecord.status)
  const totalSteps = CASE_STATUS_PIPELINE.length
  const progressPct = ((stepIndex + 1) / totalSteps) * 100

  return (
    <Card>
      <CardContent className="flex flex-col gap-4">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div className="flex min-w-0 items-start gap-3">
            <span className="bg-primary/10 text-primary flex size-11 shrink-0 items-center justify-center rounded-full">
              <Clock className="size-5" />
            </span>
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-2">
                <FlowBadge flow={caseRecord.flow} />
                <p className="truncate text-base font-semibold">{FLOW_LABEL[caseRecord.flow]}</p>
              </div>
              <p className="text-muted-foreground mt-1 text-sm">
                Still with your advisor — this unlocks once it&apos;s published.
              </p>
            </div>
          </div>
          {/* ml-14 matches the progress row's own pl-14 below (icon's size-11 + its gap-3) — so if
              this row wraps on a narrow screen, the badge lines up under the title column instead
              of sitting flush against the card's left edge, under the icon. */}
          <CaseStatusBadge status={caseRecord.status} className="ml-14" />
        </div>

        <div className="flex flex-wrap items-center gap-3 pl-14">
          <div className="flex min-w-0 flex-1 items-center gap-3">
            <Progress value={progressPct} className="h-1.5" />
            <span className="text-muted-foreground shrink-0 text-xs font-medium">
              Step {stepIndex + 1} of {totalSteps}
            </span>
          </div>
          {/* Scoped to this specific case (`?case=` param) — without it, `/review-status` falls
              back to "most recently created case", which picks the wrong one whenever the
              investor has more than one case in flight at once. */}
          <Button
            size="sm"
            onClick={() => router.push(routes.reviewStatus(caseRecord.id) as Route)}
            className="shrink-0"
          >
            Track status
          </Button>
        </div>
      </CardContent>
    </Card>
  )
}
